import {
  GetInvoiceResponse,
  InvoiceCustomChargeSummary,
  InvoiceInput,
  InvoiceLineItemInput,
  InvoiceResponse,
  InvoiceTripInput,
  InvoiceTypeTypeEnum,
  RunPreviewInvoiceResponse,
  TripStatusEnum,
} from '@/models/gen/graphql';

import createGraphApiHook from '@/hooks/createGraphApiHook';
import getInvoice from './getInvoice';
import runPreviewInvoice from './runPreviewInvoice';

interface ParsedInvoiceResponseValues {
  airports: string[];
  iataAirlineCodes: string[];
  types: InvoiceTypeTypeEnum[];
  tripStatus: TripStatusEnum[];
  emails: string[];
  headers: { name: string }[];
  items: InvoiceLineItemInput[];
  trips: InvoiceTripInput[];
  customChargeSummary: InvoiceCustomChargeSummary[];
}

// Type for Parsed Invoice response
export type ParsedInvoiceResponse = {
  [K in keyof InvoiceResponse]: K extends keyof ParsedInvoiceResponseValues ? ParsedInvoiceResponseValues[K] : InvoiceResponse[K];
};

export type ParsedInvoiceInput = {
  [K in keyof InvoiceInput]: K extends keyof ParsedInvoiceResponseValues ? ParsedInvoiceResponseValues[K] : InvoiceInput[K];
};

interface ParsedRunPreviewInvoiceResponseValues {
  output: ParsedInvoiceResponse;
  customChargeSummary: InvoiceCustomChargeSummary[];
}

// Type for Parsed Run Preview Invoice response
export type ParsedRunPreviewInvoiceResponse = {
  [K in keyof RunPreviewInvoiceResponse]: K extends keyof ParsedRunPreviewInvoiceResponseValues
    ? ParsedRunPreviewInvoiceResponseValues[K]
    : RunPreviewInvoiceResponse[K];
};

const convertGetInvoiceResponseToRunPreviewInvoiceResponse = (res: GetInvoiceResponse): RunPreviewInvoiceResponse => {
  if (!res) return;
  const output: RunPreviewInvoiceResponse = {
    ...res,
    summary: undefined,
    customChargeSummary: res.customChargesSummary || [],
    __typename: 'RunPreviewInvoiceResponse',
  };
  return output;
};
export const getInvoicePreview = async (input: number | InvoiceInput): Promise<RunPreviewInvoiceResponse> => {
  if (!input) return;
  if (typeof input === 'number') {
    const getInvoiceResponse = await getInvoice(input);
    return convertGetInvoiceResponseToRunPreviewInvoiceResponse(getInvoiceResponse);
  }

  return await runPreviewInvoice(input);
};

export const convertParsedInvoiceInputToInvoiceInput = (input: ParsedInvoiceInput): InvoiceInput => {
  const parsedInput: InvoiceInput = {
    ...input,
    // Ensure we only send the client input fields expected by the backend (do NOT include client.id)
    client: input?.client
      ? {
          name: input?.client?.name || undefined,
          address: input?.client?.address || undefined,
          providerId: input?.client?.providerId || undefined,
        }
      : undefined,
    tripStatus: (input?.tripStatus || []).map((status) => ({ status })),
    iataAirlineCodes: (input?.iataAirlineCodes || []).map((iataCode) => ({ iataCode })),
    airports: (input?.airports || []).map((code) => ({ code })),
    emails: (input?.emails || []).map((address) => ({ address })),
    headers: (input?.headers || []).map((header) => header),
    trips: (input?.trips || []).map(({ index, rate, tripId }) => ({ index, rate, tripId })),
    types: (input?.types || []).map((type) => ({ type: type || InvoiceTypeTypeEnum.Sch })),
    chargeType: input?.chargeType,
  };
  return parsedInput;
};

export const parseGetInvoicePreviewResponse = (res: RunPreviewInvoiceResponse): ParsedRunPreviewInvoiceResponse => {
  if (!res) return {};
  const parsedResponse: ParsedRunPreviewInvoiceResponse = {
    ...res,
    output: {
      ...res?.output,
      airports: (res?.output?.airports || []).map(({ code }) => code),
      iataAirlineCodes: (res?.output?.iataAirlineCodes || []).map(({ iataCode }) => iataCode),
      emails: (res?.output?.emails || []).map(({ address }) => address),
      headers: (res?.output?.headers || []).map((header) => header),
      items: (res?.output?.items || []).map(({ lineItemTypeId, name, total }) => ({ lineItemTypeId, name, total })),
      types: (res?.output?.types || []).map(({ type }) => type),
      tripStatus: (res?.output?.tripStatus || []).map(({ status }) => status as any),
    },
    customChargeSummary: (res?.customChargeSummary || []).map(({ charges, type, subtotal }) => ({ charges, type, subtotal })),
  };
  return parsedResponse;
};

export const useGetInvoicePreview = createGraphApiHook(getInvoicePreview);
